/**
 * Notification Permission Modal
 * Custom modal to request push notification permission
 */

import * as React from 'react';
import { cn } from '@/shared/utils/cn';
import { usePushNotification } from '../hooks/usePushNotification';
import { BellIcon } from '@/shared/icons/icons';

interface NotificationPermissionModalProps {
  /**
   * Custom user ID for subscription
   */
  customerId?: string;
  /**
   * Visitor ID for subscription
   */
  visitorId?: string;
  /**
   * Delay before showing modal (ms)
   * @default 500
   */
  showDelay?: number;
  /**
   * Custom heading text
   * @default "Get Updates"
   */
  heading?: string;
  /**
   * Custom description text
   */
  description?: string;
  /**
   * Custom confirm button text
   * @default "Allow"
   */
  confirmText?: string;
  /**
   * Custom deny button text
   * @default "Not now"
   */
  denyText?: string;
  /**
   * Callback when permission is granted
   */
  onAllow?: (token: string | null) => void;
  /**
   * Callback when permission is denied/dismissed
   */
  onDeny?: () => void;
  /**
   * Custom class name for the modal
   */
  className?: string;
}

export function NotificationPermissionModal({
  customerId,
  visitorId,
  showDelay = 500,
  heading = 'Get Updates',
  description = 'We promise to only send you relevant content and give you updates on your transactions',
  confirmText = 'Allow',
  denyText = 'Not now',
  onAllow,
  onDeny,
  className,
}: NotificationPermissionModalProps) {
  const [isVisible, setIsVisible] = React.useState(false);

  const {
    isSupported,
    permission,
    isLoading,
    requestPermission,
    shouldShowPrompt,
    dismissPrompt,
  } = usePushNotification({
    customerId,
    visitorId,
    autoInitialize: true,
  });

  // Show modal after delay if conditions are met
  React.useEffect(() => {
    if (!isSupported || permission !== 'default') {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (shouldShowPrompt()) {
        setIsVisible(true);
      }
    }, showDelay);

    return () => clearTimeout(timeoutId);
  }, [isSupported, permission, showDelay, shouldShowPrompt]);

  const handleAllow = async () => {
    setIsVisible(false);
    const token = await requestPermission();
    onAllow?.(token);
  };

  const handleDeny = () => {
    setIsVisible(false);
    dismissPrompt();
    onDeny?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex items-start justify-center pt-[150px]',
        'bg-black/40',
        className
      )}
      onClick={handleDeny}
    >
      <div
        className="w-[350px] rounded-[10px] shadow-[0_0_10px_rgba(0,0,0,0.5)] font-['Mulish',sans-serif]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#001e38] text-white/90 text-base uppercase py-4 px-4 rounded-t-[10px] text-left relative tracking-[0.1em]">
          {heading}
          <BellIcon className="absolute right-5 top-1/2 -translate-y-1/2 opacity-20 w-5 h-5" />
        </div>

        {/* Description */}
        <div className="bg-[#f7f7f7] text-sm py-[30px] px-4 text-left tracking-[0.025em]">
          {description}
        </div>

        {/* Buttons */}
        <div className="bg-white py-4 px-4 rounded-b-[10px] flex">
          <button
            type="button"
            onClick={handleDeny}
            disabled={isLoading}
            className="relative flex-1 cursor-pointer text-lg font-medium text-center tracking-[0.025em] text-[#817e7e] after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-[#c9c6c6]"
          >
            {denyText}
          </button>
          <button
            type="button"
            onClick={handleAllow}
            disabled={isLoading}
            className="flex-1 cursor-pointer text-lg font-medium text-center tracking-[0.025em] text-black disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

NotificationPermissionModal.displayName = 'NotificationPermissionModal';
